import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { isAcceptableYoutubeValue, youtubeThumbnailUrl } from '@/lib/youtube';
import { toast } from 'sonner';
import { 
  ChevronRight, Plus, Pencil, Trash2, Package, 
  ShoppingCart, DollarSign, LogOut, CheckCircle, Clock,
  Tag, Settings, Mail, Calendar, Users, Eye, FileText, Phone, Building, User, Upload, Youtube
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, admin, logout, getAuthHeader, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [emails, setEmails] = useState([]);
  const [adReps, setAdReps] = useState([]);
  const [settings, setSettings] = useState({ admin_email: '' });
  const [loading, setLoading] = useState(true);
  
  // Product dialog state
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    category: 'print',
    is_active: true,
    discount_percentage: '',
    discount_expires_at: '',
    youtube_url: ''
  });

  // Ad Rep dialog state
  const [editingAdRep, setEditingAdRep] = useState(null);
  const [showAdRepDialog, setShowAdRepDialog] = useState(false);
  const [adRepForm, setAdRepForm] = useState({
    name: '',
    email: ''
  });

  // Order detail dialog state
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Bulk delete state
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());

  // Product image upload
  const productImageRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Change password form
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
      fetchOrders();
      fetchSettings();
      fetchEmails();
      fetchAdReps();
    }
  }, [isAuthenticated]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?active_only=false`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: getAuthHeader()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API}/admin/emails`, {
        headers: getAuthHeader()
      });
      setEmails(response.data);
    } catch (error) {
      console.error('Error fetching emails:', error);
    }
  };

  const fetchAdReps = async () => {
    try {
      const response = await axios.get(`${API}/ad-reps`);
      setAdReps(response.data);
    } catch (error) {
      console.error('Error fetching ad reps:', error);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.next) {
      toast.error('Enter your current password and a new password');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      toast.error('The new passwords do not match');
      return;
    }
    if (passwordForm.next.length < 12) {
      toast.error('New password must be at least 12 characters');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/admin/password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.next
      }, { headers: getAuthHeader() });
      setPasswordForm({ current: '', next: '', confirm: '' });
      toast.success('Password updated');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, {
        admin_email: settings.admin_email
      }, { headers: getAuthHeader() });
      toast.success('Settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  // Product handlers
  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.description || !productForm.price || !productForm.image_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isAcceptableYoutubeValue(productForm.youtube_url)) {
      toast.error('That YouTube link is not valid. Paste a youtube.com or youtu.be video URL, or leave it empty.');
      return;
    }

    try {
      const productData = {
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price),
        image_url: productForm.image_url,
        category: productForm.category,
        is_active: productForm.is_active,
        discount_percentage: productForm.discount_percentage ? parseFloat(productForm.discount_percentage) : null,
        discount_expires_at: productForm.discount_expires_at || null,
        youtube_url: (productForm.youtube_url || '').trim() || null
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers: getAuthHeader() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, productData, { headers: getAuthHeader() });
        toast.success('Product created');
      }
      setShowProductDialog(false);
      resetProductForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: getAuthHeader()
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const openEditProductDialog = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image_url: product.image_url,
      category: product.category,
      is_active: product.is_active,
      discount_percentage: product.discount_percentage?.toString() || '',
      discount_expires_at: product.discount_expires_at ? product.discount_expires_at.split('T')[0] : '',
      youtube_url: product.youtube_url || ''
    });
    setShowProductDialog(true);
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      image_url: '',
      category: 'print',
      is_active: true,
      discount_percentage: '',
      discount_expires_at: '',
      youtube_url: ''
    });
  };

  const handleProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/products/upload-image`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      // Relative URL: works on the preview and on store.gringogazette.com alike
      setProductForm(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (productImageRef.current) productImageRef.current.value = '';
    }
  };

  // Ad Rep handlers
  const handleSaveAdRep = async () => {
    if (!adRepForm.name || !adRepForm.email) {
      toast.error('Please fill in name and email');
      return;
    }

    try {
      if (editingAdRep) {
        await axios.put(`${API}/admin/ad-reps/${editingAdRep.id}`, adRepForm, { headers: getAuthHeader() });
        toast.success('Ad Rep updated');
      } else {
        await axios.post(`${API}/admin/ad-reps`, adRepForm, { headers: getAuthHeader() });
        toast.success('Ad Rep created');
      }
      setShowAdRepDialog(false);
      resetAdRepForm();
      fetchAdReps();
    } catch (error) {
      console.error('Error saving ad rep:', error);
      toast.error('Failed to save ad rep');
    }
  };

  const handleDeleteAdRep = async (adRepId) => {
    if (!window.confirm('Are you sure you want to delete this ad rep?')) return;

    try {
      await axios.delete(`${API}/admin/ad-reps/${adRepId}`, {
        headers: getAuthHeader()
      });
      toast.success('Ad Rep deleted');
      fetchAdReps();
    } catch (error) {
      console.error('Error deleting ad rep:', error);
      toast.error('Failed to delete ad rep');
    }
  };

  const openEditAdRepDialog = (adRep) => {
    setEditingAdRep(adRep);
    setAdRepForm({
      name: adRep.name,
      email: adRep.email
    });
    setShowAdRepDialog(true);
  };

  const resetAdRepForm = () => {
    setEditingAdRep(null);
    setAdRepForm({
      name: '',
      email: ''
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalRevenue = orders
    .filter(o => o.status === 'paid' || o.status === 'completed')
    .reduce((sum, o) => sum + o.total_amount, 0);

  const productsOnSale = products.filter(p => p.is_on_sale && p.discount_valid).length;

  // Get ad rep name by ID
  const getAdRepName = (adRepId) => {
    if (!adRepId) return 'None';
    const rep = adReps.find(r => r.id === adRepId);
    return rep ? rep.name : 'Unknown';
  };

  const isDeletable = (order) => order.status !== 'paid' && order.status !== 'completed';

  const deletableOrders = orders.filter(isDeletable);
  const allDeletableSelected = deletableOrders.length > 0 && deletableOrders.every(o => selectedOrderIds.has(o.id));

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allDeletableSelected) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(deletableOrders.map(o => o.id)));
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers: getAuthHeader() });
      toast.success('Order deleted');
      setSelectedOrder(null);
      setSelectedOrderIds(prev => { const n = new Set(prev); n.delete(orderId); return n; });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrderIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedOrderIds.size} order(s)?`)) return;
    try {
      const res = await axios.post(`${API}/admin/orders/bulk-delete`, {
        order_ids: Array.from(selectedOrderIds)
      }, { headers: getAuthHeader() });
      toast.success(`${res.data.deleted} order(s) deleted${res.data.skipped ? `, ${res.data.skipped} paid order(s) skipped` : ''}`);
      setSelectedOrderIds(new Set());
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete orders');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-slate-500 hover:text-navy-700">Home</Link>
              <ChevronRight className="h-4 w-4 text-slate-400" />
              <span className="text-slate-900 font-medium">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Welcome, <span className="font-medium">{admin?.username}</span>
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={logout}
                className="text-slate-600"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8">
          Admin Dashboard
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-navy-100 rounded-lg">
                  <Package className="h-6 w-6 text-navy-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Products</p>
                  <p className="font-heading text-2xl font-bold text-slate-900" data-testid="total-products">
                    {products.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Tag className="h-6 w-6 text-red-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">On Sale</p>
                  <p className="font-heading text-2xl font-bold text-red-600">
                    {productsOnSale}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-green-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Orders</p>
                  <p className="font-heading text-2xl font-bold text-slate-900" data-testid="total-orders">
                    {orders.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-yellow-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Revenue</p>
                  <p className="font-heading text-2xl font-bold text-slate-900" data-testid="total-revenue">
                    {formatPrice(totalRevenue)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Users className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Ad Reps</p>
                  <p className="font-heading text-2xl font-bold text-slate-900">
                    {adReps.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="ad-reps" data-testid="tab-ad-reps">Ad Reps</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">Email Queue</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Products</CardTitle>
                <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-navy-900 hover:bg-navy-800"
                      onClick={() => resetProductForm()}
                      data-testid="add-product-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingProduct ? 'Edit Product' : 'Add New Product'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          placeholder="Product name"
                          data-testid="product-name-input"
                        />
                      </div>
                      <div>
                        <Label>Description *</Label>
                        <div className="mt-1 quill-wrapper" data-testid="product-description-input">
                          <ReactQuill
                            theme="snow"
                            value={productForm.description}
                            onChange={(value) => setProductForm({ ...productForm, description: value })}
                            placeholder="Product description"
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                ['link', 'blockquote'],
                                ['clean']
                              ]
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="price">Price (USD) *</Label>
                          <Input
                            id="price"
                            type="number"
                            value={productForm.price}
                            onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                            placeholder="0"
                            data-testid="product-price-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="category">Category</Label>
                          <Select
                            value={productForm.category}
                            onValueChange={(value) => setProductForm({ ...productForm, category: value })}
                          >
                            <SelectTrigger data-testid="product-category-select">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="print">Print</SelectItem>
                              <SelectItem value="digital">Digital</SelectItem>
                              <SelectItem value="social">Social</SelectItem>
                              <SelectItem value="advertorial">Advertorial</SelectItem>
                              <SelectItem value="events">Events</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Product Image *</Label>
                        <div className="mt-1 space-y-2">
                          {productForm.image_url && (
                            <div className="w-full h-32 rounded-lg overflow-hidden bg-slate-100 border">
                              <img src={productForm.image_url} alt="Preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-shrink-0"
                              onClick={() => productImageRef.current?.click()}
                              disabled={uploadingImage}
                              data-testid="product-image-upload-btn"
                            >
                              {uploadingImage ? (
                                <span className="flex items-center gap-2">
                                  <div className="spinner h-4 w-4" />
                                  Uploading...
                                </span>
                              ) : (
                                <>
                                  <Upload className="h-4 w-4 mr-2" />
                                  Upload Image
                                </>
                              )}
                            </Button>
                            <Input
                              value={productForm.image_url}
                              onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                              placeholder="Or paste image URL"
                              className="flex-1"
                              data-testid="product-image-input"
                            />
                          </div>
                          <input
                            ref={productImageRef}
                            type="file"
                            accept="image/*"
                            onChange={handleProductImageUpload}
                            className="hidden"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="youtube_url">YouTube Video (optional)</Label>
                        <Input
                          id="youtube_url"
                          value={productForm.youtube_url || ''}
                          onChange={(e) => setProductForm({ ...productForm, youtube_url: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="mt-1"
                          data-testid="product-youtube-input"
                        />
                        {(productForm.youtube_url || '').trim() && !isAcceptableYoutubeValue(productForm.youtube_url) && (
                          <p className="text-xs text-red-600 mt-1" data-testid="product-youtube-error">
                            Not a YouTube video link. Use a youtube.com/watch, youtu.be or youtube.com/shorts URL.
                          </p>
                        )}
                        {youtubeThumbnailUrl(productForm.youtube_url) && (
                          <div className="mt-2 flex items-center gap-3" data-testid="product-youtube-preview">
                            <div className="w-32 aspect-video rounded overflow-hidden bg-slate-100 border flex-shrink-0">
                              <img
                                src={youtubeThumbnailUrl(productForm.youtube_url)}
                                alt="Video preview"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Youtube className="h-4 w-4 text-red-600" />
                              Shows as a player on the product page
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-1">Leave empty for no video.</p>
                      </div>

                      <Separator />
                      
                      {/* Discount Section */}
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
                        <div className="flex items-center gap-2">
                          <Tag className="h-5 w-5 text-red-600" />
                          <h4 className="font-semibold text-red-800">Discount Settings</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="discount_percentage">Discount %</Label>
                            <Input
                              id="discount_percentage"
                              type="number"
                              min="0"
                              max="100"
                              value={productForm.discount_percentage}
                              onChange={(e) => setProductForm({ ...productForm, discount_percentage: e.target.value })}
                              placeholder="e.g. 25"
                              data-testid="product-discount-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="discount_expires_at">Expires On</Label>
                            <Input
                              id="discount_expires_at"
                              type="date"
                              value={productForm.discount_expires_at}
                              onChange={(e) => setProductForm({ ...productForm, discount_expires_at: e.target.value })}
                              data-testid="product-discount-expiry-input"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-red-600">Leave empty to remove discount</p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Switch
                          id="is_active"
                          checked={productForm.is_active}
                          onCheckedChange={(checked) => setProductForm({ ...productForm, is_active: checked })}
                          data-testid="product-active-switch"
                        />
                        <Label htmlFor="is_active">Active (visible in store)</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowProductDialog(false)}>
                        Cancel
                      </Button>
                      <Button 
                        className="bg-navy-900 hover:bg-navy-800"
                        onClick={handleSaveProduct}
                        data-testid="save-product-btn"
                      >
                        {editingProduct ? 'Save Changes' : 'Create Product'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-3 font-medium text-slate-600">Product</th>
                          <th className="pb-3 font-medium text-slate-600">Category</th>
                          <th className="pb-3 font-medium text-slate-600">Price</th>
                          <th className="pb-3 font-medium text-slate-600">Discount</th>
                          <th className="pb-3 font-medium text-slate-600">Status</th>
                          <th className="pb-3 font-medium text-slate-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {products.map((product) => (
                          <tr key={product.id} data-testid={`product-row-${product.id}`}>
                            <td className="py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded overflow-hidden bg-slate-100 relative">
                                  <img 
                                    src={product.image_url} 
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                  />
                                  {product.is_on_sale && product.discount_valid && (
                                    <div className="absolute inset-0 bg-red-600/20 flex items-center justify-center">
                                      <Tag className="h-4 w-4 text-red-600" />
                                    </div>
                                  )}
                                </div>
                                <span className="font-medium text-slate-900 line-clamp-1 max-w-[200px]">
                                  {product.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3">
                              <Badge variant="secondary" className="capitalize">
                                {product.category}
                              </Badge>
                            </td>
                            <td className="py-3">
                              {product.is_on_sale && product.discount_valid ? (
                                <div>
                                  <span className="font-medium text-red-600">{formatPrice(product.final_price)}</span>
                                  <span className="text-sm text-slate-400 line-through ml-2">{formatPrice(product.price)}</span>
                                </div>
                              ) : (
                                <span className="font-medium">{formatPrice(product.price)}</span>
                              )}
                            </td>
                            <td className="py-3">
                              {product.discount_percentage ? (
                                <div>
                                  <Badge className={product.discount_valid ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}>
                                    {product.discount_percentage}% OFF
                                  </Badge>
                                  {product.discount_expires_at && (
                                    <p className="text-xs text-slate-500 mt-1">
                                      Expires: {formatDate(product.discount_expires_at)}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-3">
                              {product.is_active ? (
                                <Badge className="bg-green-100 text-green-800">Active</Badge>
                              ) : (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                            </td>
                            <td className="py-3">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditProductDialog(product)}
                                  data-testid={`edit-product-${product.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteProduct(product.id)}
                                  data-testid={`delete-product-${product.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Recent Orders</CardTitle>
                {selectedOrderIds.size > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    data-testid="bulk-delete-orders-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete {selectedOrderIds.size} Selected
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No orders yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-3 pl-1 w-10">
                            {deletableOrders.length > 0 && (
                              <input
                                type="checkbox"
                                checked={allDeletableSelected}
                                onChange={toggleSelectAll}
                                className="h-4 w-4 rounded border-slate-300 cursor-pointer"
                                data-testid="select-all-orders-checkbox"
                              />
                            )}
                          </th>
                          <th className="pb-3 font-medium text-slate-600">Order ID</th>
                          <th className="pb-3 font-medium text-slate-600">Customer</th>
                          <th className="pb-3 font-medium text-slate-600">Ad Rep</th>
                          <th className="pb-3 font-medium text-slate-600">Items</th>
                          <th className="pb-3 font-medium text-slate-600">Total</th>
                          <th className="pb-3 font-medium text-slate-600">Status</th>
                          <th className="pb-3 font-medium text-slate-600">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {orders.map((order) => (
                          <tr 
                            key={order.id} 
                            data-testid={`order-row-${order.id}`}
                            className="cursor-pointer hover:bg-slate-50 transition-colors"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <td className="py-3 pl-1" onClick={(e) => e.stopPropagation()}>
                              {isDeletable(order) ? (
                                <input
                                  type="checkbox"
                                  checked={selectedOrderIds.has(order.id)}
                                  onChange={() => toggleOrderSelection(order.id)}
                                  className="h-4 w-4 rounded border-slate-300 cursor-pointer"
                                  data-testid={`order-checkbox-${order.id}`}
                                />
                              ) : (
                                <span className="block h-4 w-4" />
                              )}
                            </td>
                            <td className="py-3 font-mono text-sm">
                              #{order.id.slice(0, 8)}
                            </td>
                            <td className="py-3">
                              <div>
                                <div className="font-medium text-slate-900">{order.customer_info?.full_name || order.customer_name}</div>
                                <div className="text-sm text-slate-500">{order.customer_info?.email || order.customer_email}</div>
                                {order.customer_info?.company && (
                                  <div className="text-xs text-slate-400">{order.customer_info.company}</div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 text-sm text-slate-600">
                              {getAdRepName(order.ad_rep_id)}
                            </td>
                            <td className="py-3 text-sm text-slate-600">
                              {order.items.length} item(s)
                            </td>
                            <td className="py-3 font-medium">{formatPrice(order.total_amount)}</td>
                            <td className="py-3">
                              {order.status === 'paid' || order.status === 'completed' ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Paid
                                </Badge>
                              ) : order.status === 'failed' ? (
                                <Badge className="bg-red-100 text-red-800">
                                  Failed
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {order.status}
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 text-sm text-slate-600">
                              {formatDate(order.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Detail Dialog */}
            <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Order #{selectedOrder?.id?.slice(0, 8)}
                  </DialogTitle>
                </DialogHeader>
                {selectedOrder && (
                  <div className="space-y-5 py-2">
                    {/* Status & Date */}
                    <div className="flex items-center justify-between">
                      <div>
                        {selectedOrder.status === 'paid' || selectedOrder.status === 'completed' ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Paid
                          </Badge>
                        ) : selectedOrder.status === 'failed' ? (
                          <Badge className="bg-red-100 text-red-800">Failed</Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Clock className="h-3 w-3 mr-1" />
                            {selectedOrder.status}
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-slate-500">{formatDate(selectedOrder.created_at)}</span>
                    </div>

                    <Separator />

                    {/* Customer Info */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Customer Information</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex items-start gap-2">
                          <User className="h-4 w-4 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Full Name</p>
                            <p className="text-sm font-medium" data-testid="order-detail-name">
                              {selectedOrder.customer_info?.full_name || selectedOrder.customer_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Mail className="h-4 w-4 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Email</p>
                            <p className="text-sm font-medium" data-testid="order-detail-email">
                              {selectedOrder.customer_info?.email || selectedOrder.customer_email}
                            </p>
                          </div>
                        </div>
                        {selectedOrder.customer_info?.company && (
                          <div className="flex items-start gap-2">
                            <Building className="h-4 w-4 text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Company</p>
                              <p className="text-sm font-medium">{selectedOrder.customer_info.company}</p>
                            </div>
                          </div>
                        )}
                        {selectedOrder.customer_info?.phone && (
                          <div className="flex items-start gap-2">
                            <Phone className="h-4 w-4 text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Phone</p>
                              <p className="text-sm font-medium">{selectedOrder.customer_info.phone}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <Users className="h-4 w-4 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Ad Rep</p>
                            <p className="text-sm font-medium">{getAdRepName(selectedOrder.ad_rep_id)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ad Instructions */}
                    {selectedOrder.customer_info?.ad_instructions && (
                      <>
                        <Separator />
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2">Ad Instructions</h4>
                          <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap" data-testid="order-detail-instructions">
                            {selectedOrder.customer_info.ad_instructions}
                          </p>
                        </div>
                      </>
                    )}

                    {/* Uploaded Files */}
                    {selectedOrder.customer_info?.files?.length > 0 && (
                      <>
                        <Separator />
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2">Uploaded Files</h4>
                          <div className="space-y-2">
                            {selectedOrder.customer_info.files.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                  <span className="text-sm text-slate-700 truncate">{file.filename}</span>
                                  {file.size > 0 && (
                                    <span className="text-xs text-slate-400">
                                      ({file.size < 1024 ? file.size + ' B' : file.size < 1048576 ? (file.size / 1024).toFixed(1) + ' KB' : (file.size / 1048576).toFixed(1) + ' MB'})
                                    </span>
                                  )}
                                </div>
                                {file.download_url && (
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}${file.download_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-navy-700 hover:text-navy-900 text-sm font-medium flex items-center gap-1"
                                    data-testid={`download-file-${idx}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Eye className="h-3 w-3" />
                                    Download
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    <Separator />

                    {/* Order Items */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Order Items</h4>
                      <div className="space-y-2">
                        {selectedOrder.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <div>
                              <span className="font-medium text-slate-900">{item.product_name}</span>
                              <span className="text-slate-500 ml-2">x{item.quantity}</span>
                            </div>
                            <div className="text-right">
                              {item.original_price && item.discount_percentage ? (
                                <div>
                                  <span className="text-slate-400 line-through text-xs mr-2">{formatPrice(item.original_price)}</span>
                                  <span className="font-medium text-red-600">{formatPrice(item.price)}</span>
                                  <Badge className="ml-2 text-xs bg-red-100 text-red-700">{item.discount_percentage}% off</Badge>
                                </div>
                              ) : (
                                <span className="font-medium">{formatPrice(item.price * item.quantity)}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <Separator className="my-3" />
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-slate-900">Total</span>
                        <span className="font-heading text-xl font-bold text-navy-900" data-testid="order-detail-total">
                          {formatPrice(selectedOrder.total_amount)}
                        </span>
                      </div>
                    </div>

                    {/* Delete button for non-paid orders */}
                    {isDeletable(selectedOrder) && (
                      <>
                        <Separator />
                        <Button
                          variant="destructive"
                          className="w-full"
                          onClick={() => handleDeleteOrder(selectedOrder.id)}
                          data-testid="order-detail-delete-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete This Order
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Ad Reps Tab */}
          <TabsContent value="ad-reps">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Ad Representatives
                </CardTitle>
                <Dialog open={showAdRepDialog} onOpenChange={setShowAdRepDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-navy-900 hover:bg-navy-800"
                      onClick={() => resetAdRepForm()}
                      data-testid="add-ad-rep-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Ad Rep
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {editingAdRep ? 'Edit Ad Rep' : 'Add New Ad Rep'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="rep_name">Name *</Label>
                        <Input
                          id="rep_name"
                          value={adRepForm.name}
                          onChange={(e) => setAdRepForm({ ...adRepForm, name: e.target.value })}
                          placeholder="Full name"
                          data-testid="ad-rep-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="rep_email">Email *</Label>
                        <Input
                          id="rep_email"
                          type="email"
                          value={adRepForm.email}
                          onChange={(e) => setAdRepForm({ ...adRepForm, email: e.target.value })}
                          placeholder="email@gringogazette.com"
                          data-testid="ad-rep-email-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAdRepDialog(false)}>
                        Cancel
                      </Button>
                      <Button 
                        className="bg-navy-900 hover:bg-navy-800"
                        onClick={handleSaveAdRep}
                        data-testid="save-ad-rep-btn"
                      >
                        {editingAdRep ? 'Save Changes' : 'Create Ad Rep'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  When a customer selects an ad rep during checkout, the ad rep will receive a copy of the order notification email.
                  The "None" option is always available in the dropdown and cannot be edited.
                </p>
                {adReps.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No ad reps yet. Add your first ad rep to get started.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-3 font-medium text-slate-600">Name</th>
                          <th className="pb-3 font-medium text-slate-600">Email</th>
                          <th className="pb-3 font-medium text-slate-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {adReps.map((rep) => (
                          <tr key={rep.id} data-testid={`ad-rep-row-${rep.id}`}>
                            <td className="py-3 font-medium text-slate-900">
                              {rep.name}
                            </td>
                            <td className="py-3 text-slate-600">
                              {rep.email}
                            </td>
                            <td className="py-3">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditAdRepDialog(rep)}
                                  data-testid={`edit-ad-rep-${rep.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteAdRep(rep.id)}
                                  data-testid={`delete-ad-rep-${rep.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Queue Tab */}
          <TabsContent value="emails">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email Queue
                </CardTitle>
              </CardHeader>
              <CardContent>
                {emails.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No emails in queue
                  </div>
                ) : (
                  <div className="space-y-4">
                    {emails.map((email) => (
                      <div key={email.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{email.subject}</span>
                          <Badge 
                            variant="secondary"
                            className={
                              email.status === 'sent' ? 'bg-green-100 text-green-700' :
                              email.status === 'failed' ? 'bg-red-100 text-red-700' :
                              ''
                            }
                          >
                            {email.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600">To: {email.to}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatDate(email.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="max-w-md space-y-4">
                  <div>
                    <Label htmlFor="admin_email">Admin Notification Email</Label>
                    <p className="text-sm text-slate-500 mb-2">
                      Order notifications and failed payment alerts will be sent to this email
                    </p>
                    <Input
                      id="admin_email"
                      type="email"
                      value={settings.admin_email}
                      onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
                      placeholder="ads@gringogazette.com"
                      data-testid="admin-email-input"
                    />
                  </div>
                  <Button 
                    className="bg-navy-900 hover:bg-navy-800"
                    onClick={handleSaveSettings}
                    data-testid="save-settings-btn"
                  >
                    Save Settings
                  </Button>
                </div>

                <Separator />

                <div className="max-w-md space-y-4" data-testid="change-password-section">
                  <div>
                    <Label className="text-base">Change Admin Password</Label>
                    <p className="text-sm text-slate-500">
                      At least 12 characters.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="current_password">Current password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      autoComplete="current-password"
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_password">New password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      autoComplete="new-password"
                      value={passwordForm.next}
                      onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password">Confirm new password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      autoComplete="new-password"
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    className="bg-navy-900 hover:bg-navy-800"
                    onClick={handleChangePassword}
                    disabled={savingPassword}
                    data-testid="change-password-btn"
                  >
                    {savingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
